import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CommonAPIService } from '../commonAPI.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { APP_CONFIG, ConfigService } from '../config.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../notification.service';
import { RecipeLite } from '../../shared/models/recipe/recipe-lite.model';
import { TripletIdLite } from '../../shared/models/generic/id/triplet-id-lite.model';
import { BinaryLiteWithWarning, BinaryPackWithWarning } from '../../shared/models/binaries/Binary-lite-with-warning.model';
import { BinaryLite } from '../../shared/models/binaries/binary-lite.model';
import { ApplianceGroup, DataRefsByKey } from '../../shared/models/dataref.model';

@Injectable({
  providedIn: 'root',
})
export class BinariesService extends CommonAPIService {
  currentLoadedBineratorFontFileName = '';
  currentLoadedBineratorFontFileContent: number[];

  constructor(
    http: HttpClient,
    configService: ConfigService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    super(configService, http);
  }

  /**
   * Generates a binary file for a recipe.
   *
   * @param params the input params
   * @param params.recipeId the recipe ID
   * @param params.groupingId the grouping ID of the recipe
   * @param params.applianceGroup the appliance group for which the binary will be created
   * @param params.type the binary type
   * @returns an observable that will emit the backend response containing the created binary and warnings
   */
  generateForRecipe(params: {
    recipeId: TripletIdLite;
    groupingId: string;
    applianceGroup: string;
    type: string;
  }): Observable<BinaryLiteWithWarning> {
    return this.post<BinaryLiteWithWarning>(`v3/recipes/${params.recipeId.sourceSystem}/${params.recipeId.functionalId}/binaries`, null, {
      applianceGroup: params.applianceGroup,
      type: params.type,
      groupingId: params.groupingId,
    });
  }

  /**
   * get the list of existing binaries for a recipe
   *
   * @param params the input parameters
   * @param params.recipeId the recipe ID
   * @param params.groupingId the grouping ID of the recipe
   * @returns An observable that will emit the list of binaries when the backend will respond
   */
  getListForRecipe(params: { recipeId: TripletIdLite; groupingId: string }): Observable<BinaryLite[]> {
    return this.get<BinaryLite[]>(`v3/recipes/${params.recipeId.sourceSystem}/${params.recipeId.functionalId}/binaries`, {
      groupingId: params.groupingId,
    });
  }

  /**
   * Publishes a binary for a recipe
   *
   * @param params the input parameters
   * @param params.recipeId the recipe ID
   * @param params.binaryId the binary ID
   * @param params.groupingId the grouping ID of the recipe
   * @returns An observable that will emit a void event when the backend will respond
   */
  publishForRecipe(params: { recipeId: TripletIdLite; binaryId: string; groupingId: string }): Observable<void> {
    return this.put<void>(
      `v3/recipes/${params.recipeId.sourceSystem}/${params.recipeId.functionalId}/binaries/${params.binaryId}/publish`,
      null,
      { groupingId: params.groupingId }
    );
  }

  /**
   * Deletes a binary for a recipe
   *
   * @param params the input parameters
   * @param params.recipeId the recipe ID
   * @param params.binaryId the binary ID
   * @param params.groupingId the grouping ID of the recipe
   * @returns An observable that will emit a void event when the backend will respond
   */
  deleteForRecipe(params: { recipeId: TripletIdLite; binaryId: string; groupingId: string }): Observable<void> {
    return this.delete<void>(`v3/recipes/${params.recipeId.sourceSystem}/${params.recipeId.functionalId}/binaries/${params.binaryId}`, {
      groupingId: params.groupingId,
    });
  }

  /**
   * Publishes a binary for a recipe
   *
   * @param params the input parameters
   * @param params.packId the pack ID
   * @param params.binaryId the binary ID
   * @returns An observable that will emit a void event when the backend will respond
   */
  publishForPack(params: { packId: string; binaryId: string }): Observable<void> {
    return this.put<void>(`packs/${params.packId}/binaries/${params.binaryId}/publish`, null);
  }

  /**
   * get the list of existing binaries for a pack
   *
   * @param params the input parameters
   * @param params.packId the pack ID
   * @returns An observable that will emit the list of binaries when the backend will respond
   */
  getListForPack(params: { packId: string }): Observable<BinaryLite[]> {
    return this.get<BinaryLite[]>(`v2/packs/${params.packId}/binaries`);
  }

  /**
   * Deletes a binary for a pack
   *
   * @param params the input parameters
   * @param params.packId the pack ID
   * @param params.binaryId the binary ID=
   * @returns An observable that will emit a void event when the backend will respond
   */
  deleteForPack(params: { packId: string; binaryId: string }): Observable<void> {
    return this.delete<void>(`packs/${params.packId}/binaries/${params.binaryId}`);
  }

  /**
   * Helper function to retrieve app groups candidate for binary process proposal
   *
   * @param variants the current recipe variants
   * @returns a Set of string containing the eligible appliance group keys
   */
  buildApplianceGroupsCandidates(variants: RecipeLite[]): string[] {
    // Use of an intermediary Set in order to avoid duplicates
    return [
      ...new Set(
        variants
          ?.map((variant: RecipeLite): string[] => {
            // If yield unit isn't UNIT_1 or quantity isn't defined, ignore this variant
            if (variant?.yield?.unit !== 'UNIT_1' || !variant?.yield?.quantity) {
              return [];
            }

            /*  Check for variants matching config.
              This means that the variant must be present in the config, and the variant yield quantity
              must be present in the config too. */
            return variant.applianceGroups?.filter((applianceGroupKey: string): boolean | undefined =>
              this.configService.getAppConfig().applianceGroupsForBinaries[applianceGroupKey]?.includes(variant.yield.quantity)
            );
          })
          .reduce((result: string[], current: string[]): string[] => [...result, ...current], [])
      ),
    ];
  }

  /**
   * Generates a binary file for a pack.
   *
   * @param params the input params
   * @param params.packId the pack ID
   * @param params.groupingId the grouping ID of the recipe
   * @param params.applianceGroup the appliance group for which the binary will be created
   * @param params.type the binary type
   * @returns an observable that will emit the backend response containing the created binary and warnings
   */
  generateForPack(params: { packId: string; applianceGroup: string; type: string }): Observable<BinaryPackWithWarning> {
    return this.post<BinaryPackWithWarning>(`packs/${params.packId}/binaries/`, null, {
      applianceGroup: params.applianceGroup,
      type: params.type,
    });
  }

  /**
   * Helper function to retrieve app groups for binary process proposal
   *
   * @returns a Set of string containing the eligible appliance group keys
   */
  buildApplianceGroupsForBinariesPack(allApplianceGroupe: DataRefsByKey<ApplianceGroup>): { key: string; label: string }[] {
    return Object.keys(allApplianceGroupe)
      .filter((key) => Object.keys(this.configService.getAppConfig().applianceGroupsForBinaries).includes(key))
      .map((applianceGroupKey: string) => ({
        key: applianceGroupKey,
        label: allApplianceGroupe[applianceGroupKey]?.translatedName ?? applianceGroupKey,
      }));
  }

  /** SURFACE CONTROLS VALIDATION
   *
   * Exemple de calcul de la taille du texte pour un calcul sur 2 lignes:
   * - On sépare la string en 2 strings au niveau d'un retour à la ligne ou un point si il y en a.
   * Si la première sous-string produite ne tient pas sur une ligne,
   * on enlève un par un le dernier mot et on le rajoute au début de la 2ème sous-string jusqu'à ce la première sous-string tienne
   * sur la première ligne. Si le premier mot restant de la première sous-string ne tient pas sur une ligne, une erreur est levée.
   * - On répète la première étape avec la 2ème sous-string (si elle existe) qui correspond à la deuxième ligne.
   * Si une troisième sous-string est produite ainsi, on essaie de remonter un à un les premier mots de la ligne 2
   * à la fin de la ligne 1 puis les premier mot de la ligne 3 à la fin de la ligne 2.
   * - Si on arrive pas ainsi à avoir un texte qui tient sur 2 lignes, on lève une erreur.
   */
  processFontFileContent(rawContent: string): void {
    this.currentLoadedBineratorFontFileContent = [];
    const x = rawContent.split('\n');
    for (let i = 1; i < x.length; i++) {
      const y = x[i].split('\t');
      this.currentLoadedBineratorFontFileContent[parseInt(y[0], 10)] = parseInt(y[2], 10);
    }
  }

  loadFontFile(fullMarket: string, lang: string, useCase: string): Observable<any> {
    const market = fullMarket.split('_')[1];
    const langMarketCode = lang + '-' + market;
    let fontName = APP_CONFIG.binerator.langMarketFontFileMap[langMarketCode];
    // Handle special cases
    if ((useCase === 'shortTitle' || useCase === 'ingredient') && market === 'JP') {
      fontName = 'Arial_Unicode_MS_13.tsv';
    } else if (useCase === 'step' && (market === 'RU' || market === 'UA')) {
      fontName = 'Arial_Narrow_15.tsv';
    }
    if (this.currentLoadedBineratorFontFileName !== fontName) {
      if (fontName !== undefined) {
        return this.httpGetFile('assets/resources/binerator/fonts-tables/' + fontName).pipe(
          tap((response: any) => {
            this.currentLoadedBineratorFontFileName = fontName;
            this.processFontFileContent(response);
          })
        );
      }
    }
    return of(null); // $q.resolve();
    // TODO
  }

  textSizePixels(text: string): number {
    let totalLength = 0;
    if (text !== undefined) {
      for (let i = 0; i < text.length; i++) {
        const uCode = text.charCodeAt(i);
        let additionalLength = 0;
        if (this.currentLoadedBineratorFontFileContent[uCode]) {
          additionalLength += this.currentLoadedBineratorFontFileContent[uCode];
        }
        totalLength += additionalLength;
      }
    }
    return totalLength;
  }

  pop1stSentence(text: string): string[] {
    let pos = ('' + text).indexOf('\n');
    if (pos === -1) {
      pos = ('' + text).indexOf('.');
    }
    if (pos === -1) {
      return [('' + text).trim(), ''];
    }
    return [('' + text).slice(0, pos + 1).trim(), ('' + text).slice(pos + 1).trim()];
  }

  popLastWord(sentence: string): string[] {
    const parts = sentence.split(' ');
    if (parts.length < 2) {
      return ['', sentence];
    }
    return [
      parts[parts.length - 1].trim(),
      parts
        .slice(0, parts.length - 1)
        .join(' ')
        .trim(),
    ];
  }

  popFirstWord(sentence: string): string[] {
    const parts = sentence.split(' ');
    if (parts.length < 2) {
      return [sentence, ''];
    }
    return [parts[0].trim(), parts.slice(1, parts.length).join(' ').trim()];
  }

  moveOneWordUp(outputText: string[], currentLine: number, maxWidth: number): boolean {
    let first;
    let tmpRemaining;
    // eslint-disable-next-line prefer-const
    [first, tmpRemaining] = this.popFirstWord(outputText[currentLine]);
    const tmpText = outputText[currentLine - 1] + ' ' + first;
    if (this.textSizePixels(tmpText) < maxWidth) {
      outputText[currentLine - 1] = tmpText;
      if (tmpRemaining.length) {
        outputText[currentLine] = tmpRemaining;
      } else {
        outputText.splice(currentLine, 1);
      }
      return true;
    }
    return false;
  }

  /**
   * Function fitOnScreen: Determine if a text fits on the cookeo screen
   */
  fitOnScreen(text: string, maxWidth: number, maxLines: number): boolean {
    if (!text) {
      // Empty text case
      return true;
    }

    const outputText = [];
    let remaining = text;

    while (remaining !== '') {
      const parts = this.pop1stSentence(remaining);
      let sentence = parts[0];
      remaining = parts[1];
      let lineWidth = this.textSizePixels(sentence);

      while (lineWidth > maxWidth) {
        const lineParts = this.popLastWord(sentence.trim());
        const last = lineParts[0];
        sentence = lineParts[1];
        if (last === '') {
          return false;
        }
        remaining = (last + ' ' + remaining).trim();
        lineWidth = this.textSizePixels(sentence);
      }
      if (outputText.length < maxLines) {
        outputText.push(sentence);
      } else {
        const nbLines = outputText.length;
        let currentLine = nbLines - 1;
        outputText[currentLine] += ' ' + sentence;
        while (this.textSizePixels(outputText[currentLine]) > maxWidth) {
          while (currentLine > 0) {
            if (this.moveOneWordUp(outputText, currentLine, maxWidth)) {
              currentLine = outputText.length - 1;
              break;
            } else {
              currentLine--;
            }
          }
          if (currentLine === 0) {
            return false;
          }
        }
      }
    }
    return true;
  }

  /**
   * getArabicFontText: call service for get transformed cookeo arabic text
   */
  getArabicFontText(fieldValue: string): Observable<any> {
    // exemple {  "sentences": ["أكتيفراي دجاج مشو"] }
    // return response.outSentences
    return this.httpPost(
      'https://9f0w0k94d8.execute-api.eu-west-1.amazonaws.com/default/BidiConvert',
      JSON.stringify({ sentences: [fieldValue] })
    );
  }

  /**
   * isFieldLengthValid: Determine if the field value matches a
   * text that fits on the Cookeo screen
   */
  isFieldLengthValid(recipeMarket: string, recipeLang: string, fieldName: string, fieldValue: string, stepType?: string): Observable<any> {
    let maxLines = 3;
    let maxWidth = 314;
    return new Observable<any>((observer: any) => {
      try {
        if (recipeLang === 'ar') {
          this.loadFontFile(recipeMarket, recipeLang, fieldName).subscribe(() =>
            this.getArabicFontText(fieldValue).subscribe((txt: any) => {
              txt = txt && txt.outSentences ? txt.outSentences[0] : '';
              switch (fieldName) {
                case 'title':
                  observer.next(this.fitOnScreen(txt, 230, 1));
                  break;
                case 'shortTitle':
                  const market = recipeMarket.split('_')[1];
                  // Subtract "4 pers" string
                  maxWidth -= this.textSizePixels(APP_CONFIG.binerator.fourPersStringMap[recipeLang + '-' + market]);
                  observer.next(this.fitOnScreen(txt, maxWidth, 1));
                  break;
                case 'applianceDescription':
                  if (stepType === 'KEEP_WARM') {
                    maxLines = 2;
                  }
                  observer.next(this.fitOnScreen(txt, 320, maxLines));
                  break;
                default:
                  observer.next(true);
                  break;
              }
              return observer.complete();
            })
          );
        } else {
          this.loadFontFile(recipeMarket, recipeLang, fieldName).subscribe(
            () => {
              switch (fieldName) {
                case 'title':
                  observer.next(this.fitOnScreen(fieldValue, 230, 1));
                  break;
                case 'shortTitle':
                  const market = recipeMarket.split('_')[1];
                  // Special case for japanese
                  if (recipeLang === 'ja' && market === 'JP') {
                    maxWidth++;
                  }
                  // Subtract "4 pers" string
                  maxWidth -= this.textSizePixels(APP_CONFIG.binerator.fourPersStringMap[recipeLang + '-' + market]);
                  observer.next(this.fitOnScreen(fieldValue, maxWidth, 1));
                  break;
                case 'applianceDescription':
                  if (stepType === 'KEEP_WARM') {
                    maxLines = 2;
                  }
                  observer.next(this.fitOnScreen(fieldValue, 320, maxLines));
                  break;
                default:
                  observer.next(true);
                  break;
              }
              return observer.complete();
            },
            (err: any) => {
              observer.error('Error in loadFontFile', err);
              observer.complete();
            }
          );
        }
      } catch (err) {
        console.error(err);
        observer.error(err);
        observer.complete();
      }
      // When the consumer unsubscribes, clean up data ready for next subscription.
      return {
        unsubscribe: () => {
          //Empty
        },
      };
    });
  }

  handleBineratorErrors(err: HttpErrorResponse): void {
    let errorText: string;
    if (err.status && err.status === 403) {
      errorText = this.translateService.instant('BINARIES.MSG.ERROR.FORBIDDEN');
      this.notificationService.sendAPIError(err, errorText);
    } else {
      this.notificationService.sendAPIError(err);
    }
  }
}
